// Employee activity tracker — writes to /employeeActivity/{uid}/.
//
// All helpers fail SILENTLY: tracking must never block the calling user-flow,
// and a denied write (e.g. for a user who is not in /employees and not in
// /admins) is acceptable — we just drop the event.
//
// Storage shape:
//   /employeeActivity/{uid}/sessions/{sessionId}/  → {loginAt, logoutAt, duration, device}
//   /employeeActivity/{uid}/actions/{pushKey}/     → {type, timestamp, ...details}

import db from '../services/db';

// In-memory session reference for the current tab. Set on login, used on logout.
let currentSession = null;   // { uid, sessionId, loginAt }

// ── Action types (used as the discriminator in /actions/{id}.type) ──────────
export const ACTIVITY = Object.freeze({
  PRODUCT_ADDED:       'product_added',
  PRODUCT_EDITED:      'product_edited',
  PRODUCT_DELETED:     'product_deleted',
  CATEGORY_ADDED:      'category_added',
  BRAND_ADDED:         'brand_added',
  INQUIRY_ASSIGNED:    'inquiry_assigned',
  INQUIRY_STATUS:      'inquiry_status_changed',
  INQUIRY_DONE:        'inquiry_done',
  INQUIRY_TRY_AGAIN:   'inquiry_try_again',
  INQUIRY_CANCELLED:   'inquiry_cancelled',
  RECOVERY_ASSIGNED:   'recovery_assigned',
  RECOVERY_DONE:       'recovery_done',
  RECOVERY_PENDING:    'recovery_pending',
});

// Log a single action. Returns nothing — caller never awaits the result.
// `data` should already be a flat plain object (no functions / undefined).
export function logActivity(uid, type, data = {}) {
  if (!uid || !type) return;
  try {
    const cleaned = {};
    Object.entries(data).forEach(([k, v]) => {
      if (v !== undefined && v !== null) cleaned[k] = v;
    });
    db.pushSubdoc('employeeActivity', uid, 'actions', {
      type,
      timestamp: Date.now(),
      ...cleaned,
    }).catch(() => { /* swallow */ });
  } catch (_) {
    /* swallow */
  }
}

// Open a session record. Stores the in-memory ref so endSession can close it.
// Idempotent for a given uid: a second call replaces the in-memory ref but
// also closes any prior in-memory session first so we don't leak open sessions
// across hot-reloads.
export async function startSession(uid) {
  if (!uid) return;
  // Close prior session if any (e.g., StrictMode dev double-mount).
  if (currentSession && currentSession.uid === uid && currentSession.sessionId) {
    try { await endSession(); } catch (_) {}
  }
  try {
    const loginAt = Date.now();
    const sessionId = await db.pushSubdoc('employeeActivity', uid, 'sessions', {
      loginAt,
      device: typeof navigator !== 'undefined' ? (navigator.userAgent || '') : '',
    });
    currentSession = { uid, sessionId, loginAt };
  } catch (_) {
    /* swallow — tracking failure must not break login */
  }
}

// Close the in-memory session with a logoutAt timestamp + duration.
export async function endSession() {
  if (!currentSession) return;
  const { uid, sessionId, loginAt } = currentSession;
  currentSession = null;   // optimistic — clear before the await
  try {
    const logoutAt = Date.now();
    const duration = Math.max(0, Math.round((logoutAt - (loginAt || logoutAt)) / 1000));
    await db.updateSubdoc('employeeActivity', uid, 'sessions', sessionId, {
      logoutAt,
      duration,
    });
  } catch (_) {
    /* swallow */
  }
}

// Best-effort session close on page unload — fire-and-forget.
export function attachUnloadHandler() {
  if (typeof window === 'undefined') return;
  const handler = () => {
    if (!currentSession) return;
    const { uid, sessionId, loginAt } = currentSession;
    const logoutAt = Date.now();
    const duration = Math.max(0, Math.round((logoutAt - (loginAt || logoutAt)) / 1000));
    try {
      // No await — browser is unloading. Firebase queues the write; in
      // practice this often arrives, but we don't depend on it.
      db.updateSubdoc('employeeActivity', uid, 'sessions', sessionId, {
        logoutAt,
        duration,
      });
    } catch (_) {}
  };
  window.addEventListener('beforeunload', handler);
  return () => window.removeEventListener('beforeunload', handler);
}

// Read-only accessor for components that need to display "you're being tracked
// as session X" (currently unused but kept for completeness).
export function currentSessionId() {
  return currentSession?.sessionId || null;
}
